/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AccountManager } from "@app/_core/account/account.manager";
import { HttpStatus, httpStatusMessages } from "@app/_core/api/http-status.enum";
import StorageHelper from "@app/_core/helpers/storage.helper";
import { catchError, EMPTY, from, Observable, Subject, switchMap, take, throwError } from "rxjs";
import { HttpResponse } from "../api/http-response.interface";
import { NotyService } from "./zorro-notification.helper";

@Injectable({
	providedIn: "root",
})
export class UnauthorizedInterceptor implements HttpInterceptor {
	private refreshRequestEndStream: Subject<void> = new Subject<void>();
	private isRefreshRequestHappening = false;

	constructor(
		private accountManager: AccountManager,
		private notificationService: NotyService,
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(req).pipe(
			catchError((err: HttpErrorResponse) => {
				if (err.status === 403 && !req.url.includes("/Authenticate")) {
					const errorResponse = err.error as HttpResponse<any>;

					if (errorResponse.status === HttpStatus.ACCOUNT_NOT_ACTIVE) {
						this.notificationService.error(
							httpStatusMessages[HttpStatus.ACCOUNT_NOT_ACTIVE].title,
							httpStatusMessages[HttpStatus.ACCOUNT_NOT_ACTIVE].message,
						);

						this.accountManager.signOut(true);

						return EMPTY;
					}
				}

				if (err.status === 401 && !req.url.includes("/RefreshToken")) {
					return from(this.handleReauthorization(req)).pipe(
						switchMap((request) => {
							if (request) {
								return next.handle(request);
							}

							this.accountManager.signOut(true);

							return EMPTY;
						}),
					);
				} else if (err.status === 401 && req.url.includes("/refreshToken")) {
					this.accountManager.signOut(true);
				}
				return throwError(() => err);
			}),
		);
	}

	private async handleReauthorization(request: HttpRequest<any>): Promise<HttpRequest<any>> {
		StorageHelper.saveToken("");
		if (StorageHelper.getRefreshToken()) {
			if (this.isRefreshRequestHappening) {
				await this.refreshRequestEndStream.pipe(take(1)).toPromise();
				return this.addToken(request, StorageHelper.getToken());
			}
			this.isRefreshRequestHappening = true;

			if ((await this.accountManager.refreshToken()).status === HttpStatus.SUCCESS) {
				this.isRefreshRequestHappening = false;
				this.refreshRequestEndStream.next();
				return this.addToken(request, StorageHelper.getToken());
			}
		} else {
			this.notificationService.error("Session expired", "Please log in again");
		}

		this.isRefreshRequestHappening = false;
		StorageHelper.killSession();
		return null;
	}

	private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
		return request.clone({
			setHeaders: {
				Authorization: token,
			},
		});
	}
}
